// @noflow
import { cloneDeep } from 'lodash';
import type {
  Action,
  State,
  OnDemandCarouselPayload,
  FranchisesPayload,
  Show,
  Movie,
} from './types';

export const initialState: State = {
  carousels: {
    fetching: false,
    error: false,
    content: [],
  },
  list: {
    fetching: false,
    error: false,
    content: {
      items: [],
      filters: {},
      sorts: [],
      count: -1,
      startingLetters: {},
      totalCount: -1,
    },
  },
  currentFilters: null,
  letter: '',
  whatsHot: {
    fetching: false,
    error: false,
    content: [],
    requestId: 0,
  },
};

const getCleanFilters = payload => ({
  ratings: payload?.filters?.ratings,
  genres: payload?.filters?.genres,
});

// this function doesn't match what we get back from radish
const mapContentCategory = (payload: OnDemandCarouselPayload) => {
  const category = payload.movies || payload.shows || [];
  return category.map(item => item[Object.keys(item)[0]]); // each item has only one property and its name is one of the categories (movie', 'show')
};

const updateListData = (payload: OnDemandCarouselPayload, items: Array<Show | Movie>) => {
  // TODO: clean up this return in Radish, should not return as one of these ones: 'movies', 'shows'.
  const newItems = Array.isArray(payload) ? payload : payload.franchises;

  const allItems = [...items, ...newItems];
  const content = {
    items: allItems,
    filters: Array.isArray(payload) ? undefined : getCleanFilters(payload),
    sorts: Array.isArray(payload) ? undefined : payload.sorts,
    totalCount: Array.isArray(payload)
      ? 1000 // This is the hardcoded value for the responses, which don't contain info about their total count. Assuming, users won't scroll more, than 1000 items...
      : payload.count,
    count: allItems.length,
  };

  return content;
};

export const updateAllOnDemandData = (payload: FranchisesPayload, items: Array<Show | Movie>) => {
  const { sorts, franchises, starting_letters: startingLetters, count } = payload;
  const allItems = [...items, ...franchises];

  return {
    items: allItems,
    count: allItems.length,
    filters: payload.filters ? getCleanFilters(payload) : undefined,
    sorts,
    startingLetters,
    totalCount: count,
  };
};

const reducer = (state: State = initialState, action: Action): State => {
  switch (action.type) {
    case 'RESET_FILTERS':
      return {
        ...state,
        currentFilters: null,
      };
    case 'UPDATE_FILTERS':
      return {
        ...state,
        currentFilters: cloneDeep(action.payload),
      };
    case 'REQUEST_CAROUSELS':
      return {
        ...state,
        carousels: {
          fetching: true,
          error: false,
          content: [],
        },
      };
    case 'RECEIVE_CAROUSELS':
      return {
        ...state,
        carousels: {
          fetching: false,
          error: false,
          content: action.payload,
        },
      };
    case 'REQUEST_CAROUSELS_ERROR':
      return {
        ...state,
        carousels: {
          fetching: false,
          error: true,
          content: [],
        },
      };
    case 'REQUEST_LIST':
      return {
        ...state,
        list: {
          ...state.list,
          fetching: true,
          error: false,
        },
      };
    case 'RECEIVE_LIST':
      return {
        ...state,
        list: {
          fetching: false,
          error: false,
          content: updateListData(action.payload, action.append ? state.list.content.items : []),
        },
      };
    case 'REQUEST_LIST_ERROR':
      return {
        ...state,
        list: {
          ...state.list,
          fetching: false,
          error: true,
        },
      };
    case 'REQUEST_ALL_ON_DEMAND':
      return {
        ...state,
        list: {
          ...state.list,
          content: action.resetContent ? initialState.list.content : state.list.content,
          fetching: true,
          error: false,
        },
      };
    case 'RECEIVE_ALL_ON_DEMAND':
      return {
        ...state,
        list: {
          fetching: false,
          error: false,
          content: updateAllOnDemandData(
            action.payload,
            action.append ? state.list.content.items : [],
          ),
        },
      };
    case 'RECEIVE_ALL_ON_DEMAND_ERROR':
      return {
        ...state,
        list: {
          ...state.list,
          fetching: false,
          error: true,
        },
      };

    case 'REQUEST_KIDS_ON_DEMAND':
      return {
        ...state,
        list: {
          ...state.list,
          fetching: true,
          error: false,
        },
      };
    case 'RECEIVE_KIDS_ON_DEMAND':
      return {
        ...state,
        list: {
          fetching: false,
          error: false,
          content: updateAllOnDemandData(
            action.payload,
            action.append ? state.list.content.items : [],
          ),
        },
      };
    case 'RECEIVE_KIDS_ON_DEMAND_ERROR':
      return {
        ...state,
        list: {
          ...state.list,
          fetching: false,
          error: true,
        },
      };
    case 'REQUEST_WHATS_HOT':
      return {
        ...state,
        whatsHot: {
          fetching: true,
          error: false,
          content: [],
          requestId: 0,
        },
      };
    case 'RECEIVE_WHATS_HOT':
      return {
        ...state,
        whatsHot: {
          fetching: false,
          error: false,
          content: action.payload,
          requestId: 0,
        },
      };
    case 'RETRIEVE_WHATS_HOT':
      return {
        ...state,
        whatsHot: {
          fetching: false,
          error: false,
          content: action.payload,
          requestId: 0,
        },
      };
    case 'RECEIVE_WHATS_HOT_ERROR':
      return {
        ...state,
        whatsHot: {
          ...state.whatsHot,
          fetching: false,
          error: true,
          requestId: 0,
        },
      };
    case 'RETRIEVE_WHATS_HOT_ERROR':
      return {
        ...state,
        whatsHot: {
          ...state.whatsHot,
          fetching: false,
          error: true,
          requestId: 0,
        },
      };
    default:
      /* Following line assures switch exhaustiveness */
      (action.type: empty); /* eslint-disable-line */
      return state;
  }
};

export default reducer;
